/**
 *
 * Block
 *
 */

import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get, omit } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { Button, Card, ListGroup } from 'react-bootstrap';
import DeleteButton from 'components/interaction/DeleteButton/index.jsx';
import ExpandButton from 'components/interaction/ExpandButton/index.jsx';
import { VideoConfContext } from 'components/VideoConf/index.js';
import setIn from 'utils/setIn.js';
import { makeDateTimeString } from 'utils/eventTimes.js';
import EventTime from '../Period/BlockModal/EventTime.jsx';
import messages from './messages.js';
import { NumbersSpan } from './NumbersSpan.js';
import { SLOT_ASSIGNED, SLOT_BLOCKED, SLOT_BREAK, SLOT_CLASSNAMES, SLOT_OPEN } from '../../constants.js';
import MeetingParticipants from '../MeetingParticipants.jsx';
import { LOCATION_TYPE_CHOOSE, LOCATION_TYPE_VIDEO } from '../SelectLocationType/constants.js';
import AssignModal from './AssignModal/index.jsx';
import VideoLinkControl from './VideoLinkControl.jsx';
import useSelectLocationType from '../SelectLocationType/useSelectLocationType.jsx';
import useMeetingLocationType from '../../hooks/useMeetingLocationType.js';
import useSaveTimeslots from '../../hooks/useSaveTimeslots.js';

const ListGroupRow = styled(ListGroup.Item)`
  .btn.btn {
    padding-top: 0;
    padding-bottom: 0;
  }
  &.slot-break {
    color: ${(p) => p.theme.gray};
    background-color: ${(p) => p.theme.grayLighter};
  }
  &.slot-assigned {
    background-color: ${(p) => p.theme.stateSuccessBg};
  }
  &.slot-blocked {
    background-color: ${(p) => p.theme.stateDangerBg};
  }
  display: flex;
  .col-sm {
    flex: 0 0 5%;
  }
  .col-names {
    flex: 0 0 30%;
  }
  .col-location {
    flex: 0 0 25%;
  }
  .col-link {
    flex: 1 1 30%;
  }
`;

function Block(props) {
  const { meetingSet, blockId, expanded, onToggle, selectTypeId, setSelectTypeId, onChange, meetingList } = props;
  const { blocks, meetings } = meetingSet;
  const block = blocks[blockId];
  const { start, end, timeslots } = block;
  const { enabled, removeVideo, lockedVideo } = useContext(VideoConfContext);
  const showLocationType = useSelectLocationType();
  const countAvailable = timeslots.filter((slot) => slot.status === SLOT_OPEN).length;
  const { updateTimeslot } = useSaveTimeslots();
  const { selectMeetingLocationType } = useMeetingLocationType();
  const [assignSlot, setAssignSlot] = useState(null);

  async function handleSelectType(meeting) {
    await onChange(meeting);
    setSelectTypeId(0);
  }

  async function setDefaultType(meetingId) {
    const meeting = meetings[meetingId];
    let { meetingLocationType } = meetingSet;
    if (meetingLocationType === LOCATION_TYPE_CHOOSE) {
      meetingLocationType = await selectMeetingLocationType(meeting.meetingLocationType);
      if (!meetingLocationType) {
        return false;
      }
    }
    if (meeting.meetingLocationType !== meetingLocationType) {
      await onChange({ ...meeting, meetingLocationType });
    }
    return true;
  }

  function handleChangeSlot(slot) {
    return updateTimeslot(meetingSet.id, slot);
  }

  function middleColumn(slot) {
    const { id, status, meeting } = slot;
    switch (status) {
      case SLOT_ASSIGNED:
        return enabled && showLocationType({
          meeting,
          onEdit: () => setSelectTypeId(id),
          showEdit: selectTypeId === id,
          onChange: handleSelectType,
        });
      case SLOT_BREAK:
        return <FormattedMessage {...messages.break} />;
      case SLOT_BLOCKED:
        return <FormattedMessage {...messages.blocked} />;
      case SLOT_OPEN:
        return (
          <>
            <Button variant="link" className="ps-0" onClick={() => setAssignSlot(slot)}>
              <FormattedMessage {...messages.assign} />
            </Button>
            <span>|</span>
            <Button variant="link" onClick={() => handleChangeSlot({ ...slot, status: SLOT_BLOCKED })}>
              <FormattedMessage {...messages.block} />
            </Button>
          </>
        );
      default:
        return (
          <span className="small text-muted">
            {id}
            :
            {' '}
            {status}
          </span>
        );
    }
  }

  function videoLink(slot) {
    const { meeting } = slot;
    if (!enabled || !meeting || meeting.meetingLocationType !== LOCATION_TYPE_VIDEO) {
      return null;
    }
    return <VideoLinkControl meeting={meeting} onChange={(videoMeeting) => onChange({ ...meeting, videoMeeting })} />;
  }

  function cancelSlot(slot) {
    const { status, meeting } = slot;
    const videoInfo = get(meeting, 'videoMeeting.info');
    const title = lockedVideo(videoInfo);
    function handleDelete() {
      if (title) {
        return;
      }
      const isLastSlot = meeting.details.slots.length === 1;
      handleChangeSlot({ ...slot, status: SLOT_OPEN, meetingId: null });
      if (videoInfo) {
        removeVideo(meeting, slot.id);
        if (isLastSlot) {
          onChange({ ...meeting, videoMeeting: null });
        } else {
          const eventIds = omit(videoInfo.eventIds, [slot.id]);
          onChange(setIn(meeting, 'videoMeeting.info.eventIds', eventIds));
        }
      }
    }
    switch (status) {
      case SLOT_ASSIGNED: {
        const confirmText = { ...messages.confirmCancelMeeting, values: meeting.details.student };
        return (
          <DeleteButton
            onClick={handleDelete}
            confirmText={confirmText}
            disabled={Boolean(title)}
            title={title}
          />
        );
      }
      case SLOT_BLOCKED:
        return <DeleteButton onClick={() => handleChangeSlot({ ...slot, status: SLOT_OPEN })} />;
      default:
        return null;
    }
  }

  async function handleAssign(meetingId) {
    if (await setDefaultType(meetingId)) {
      await handleChangeSlot({ ...assignSlot, meetingId, status: SLOT_ASSIGNED });
    }
    setAssignSlot(null);
  }

  return (
    <Card className="border">
      <Card.Header className="d-flex align-items-center">
        <h4 className="text-capitalize my-2 flex-grow-1">{makeDateTimeString({ start, end })}</h4>
        <span className="text-muted">
          <NumbersSpan>
            {countAvailable}
            /
            {timeslots.length}
          </NumbersSpan>
          <FormattedMessage {...messages.slotsAvailable} />

        </span>
        <ExpandButton expanded={expanded} onClick={onToggle} />
      </Card.Header>
      {expanded && (
        <ListGroup className="border-top">
          {timeslots.map((slot) => (
            <ListGroupRow key={slot.id} className={SLOT_CLASSNAMES[slot.status]}>
              <div className="col-sm"><EventTime {...slot} className="me-3" /></div>
              <div className="col-names">{slot.meeting && <MeetingParticipants {...slot.meeting.details} />}</div>
              <div className="col-location text-nowrap">{middleColumn(slot)}</div>
              <div className="col-link">{videoLink(slot)}</div>
              <div className="col-sm">{cancelSlot(slot)}</div>
            </ListGroupRow>
          ))}
        </ListGroup>
      )}
      <AssignModal
        show={Boolean(assignSlot)}
        onHide={() => setAssignSlot(null)}
        slot={assignSlot}
        meetingList={meetingList}
        onChange={handleAssign}
      />
    </Card>
  );
}

Block.propTypes = {
  meetingSet: PropTypes.object,
  meetingList: PropTypes.array,
  blockId: PropTypes.number,
  expanded: PropTypes.bool,
  selectTypeId: PropTypes.number,
  setSelectTypeId: PropTypes.func,
  onToggle: PropTypes.func,
  onChange: PropTypes.func,
};

export default Block;
